import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Chip,
  Button,
  TextField,
  Card,
  CardContent,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Autocomplete,
  Breadcrumbs,
  Link,
  Tabs,
  Tab,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ExpandMore as ExpandMoreIcon,
  Person as PersonIcon,
  Send as SendIcon,
  Timeline as TimelineIcon,
  Security as SecurityIcon,
  Comment as CommentIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Assignment as AssignmentIcon,
  FolderOpen as CaseIcon,
  Home as HomeIcon,
  BugReport as DetectionIcon,
  Delete as DeleteIcon,
  OpenInNew as OpenInNewIcon,
  Block as SuppressIcon,
  CheckCircle as UnsuppressIcon,
  AutoAwesome as AIIcon,
  Computer as HostIcon,
  Lock as IsolateIcon,
  LockOpen as ReleaseIcon,
  CloudUpload as PushIcon,
  Label as TagIcon,
  Fingerprint as IndicatorIcon,
  Devices as EntityIcon,
  Attachment as EvidenceIcon,
  Add as AddIcon,
  Shield as ThreatIntelIcon,
  Refresh as RefreshIcon,
  AccountTree as RelatedIcon,
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { apiService } from '../services/apiService';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { usePermission } from '../hooks/usePermission';
import useAiStatus from '../hooks/useAiStatus';
import { formatDateTime, formatRelativeTime } from '../utils/dateUtils';
import IntelResultList from '../components/Intel/IntelResultList';
import { verdictMeta } from '../components/Intel/verdict';
import AskAiPanel, { renderMarkdown } from '../components/AI/AskAiPanel';

const EMPTY_OPTIONS = { statuses: [], severities: [], categories: [], sources: [] };
const EVIDENCE_TYPES = ['Log', 'Screenshot', 'File', 'File Hash', 'Network Capture', 'URL', 'Email', 'Note', 'Other'];
const VENDOR_LABELS = { crowdstrike: 'CrowdStrike', sentinelone: 'SentinelOne', microsoft_defender: 'Microsoft Defender' };

const formFromDetection = (d) => ({
  status: d?.status || '',
  assignedTo: d?.assignedTo?._id || (typeof d?.assignedTo === 'string' ? d.assignedTo : '') || '',
  severity: d?.severity || '',
  category: d?.category || '',
  subcategory: d?.subcategory || '',
  case: d?.case?._id || (typeof d?.case === 'string' ? d.case : '') || ''
});

const personName = (p, fallback = 'System') => (p && (p.firstName || p.lastName) ? `${p.firstName || ''} ${p.lastName || ''}`.trim() : fallback);

const Field = ({ label, value, mono = false }) => (
  value === undefined || value === null || value === '' ? null : (
    <Typography variant="body2" gutterBottom sx={{ wordBreak: 'break-word', fontFamily: mono ? 'monospace' : undefined }}>
      <strong>{label}:</strong> {String(value)}
    </Typography>
  )
);

export const DetectionDetailPage = () => {
  const { id: detectionId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { getSeverityColor, getStatusColor } = useSettings();
  const { hasPermission, canManageAgents } = usePermission();
  const aiStatus = useAiStatus();

  const canUpdate = hasPermission('detections', 'update');
  const canDelete = hasPermission('detections', 'delete');
  const canCreateCase = hasPermission('cases', 'create');

  // Core state
  const [detection, setDetection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [options, setOptions] = useState(EMPTY_OPTIONS);
  const [users, setUsers] = useState([]);
  const [cases, setCases] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const notify = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  // Case management
  const [editingCaseManagement, setEditingCaseManagement] = useState(false);
  const [caseManagementForm, setCaseManagementForm] = useState(formFromDetection(null));
  const [savingCaseManagement, setSavingCaseManagement] = useState(false);
  const [creatingCase, setCreatingCase] = useState(false);

  // Comments
  const [newComment, setNewComment] = useState('');
  const [addingComment, setAddingComment] = useState(false);

  // Tags
  const [editingTags, setEditingTags] = useState(false);
  const [tagsDraft, setTagsDraft] = useState([]);
  const [savingTags, setSavingTags] = useState(false);

  // Suppression
  const [suppressDialogOpen, setSuppressDialogOpen] = useState(false);
  const [suppressForm, setSuppressForm] = useState({ reason: '', until: '' });
  const [savingSuppression, setSavingSuppression] = useState(false);

  // Evidence
  const [evidenceForm, setEvidenceForm] = useState({ type: 'Note', description: '', value: '' });
  const [showEvidenceForm, setShowEvidenceForm] = useState(false);
  const [savingEvidence, setSavingEvidence] = useState(false);

  // Vendor / AI / delete actions
  const [analyzing, setAnalyzing] = useState(false);
  const [enrichingIntel, setEnrichingIntel] = useState(false);
  const [isolating, setIsolating] = useState(false);
  const [pushingStatus, setPushingStatus] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Related detections
  const [relatedDetections, setRelatedDetections] = useState([]);
  const [relatedTotal, setRelatedTotal] = useState(0);
  const [relatedLoading, setRelatedLoading] = useState(false);
  const [relatedError, setRelatedError] = useState('');

  // ---------- loading ----------
  const fetchDetection = useCallback(async () => {
    try {
      const response = await apiService.detections.getById(detectionId);
      const detectionData = response.data.detection || response.data;
      setDetection(detectionData);
      setCaseManagementForm(formFromDetection(detectionData));
      setTagsDraft(detectionData.tags || []);
      setError('');
    } catch (err) {
      console.error('Error fetching detection:', err);
      setError(err.response?.data?.message || 'Failed to load detection details');
    } finally {
      setLoading(false);
    }
  }, [detectionId]);

  useEffect(() => {
    if (!detectionId) return;
    setLoading(true);
    fetchDetection();
  }, [detectionId, fetchDetection]);

  useEffect(() => {
    apiService.detections.getOptions()
      .then((res) => setOptions({ ...EMPTY_OPTIONS, ...res.data }))
      .catch((err) => console.error('Error loading detection options:', err));
    apiService.users.getAll({ limit: 100 })
      .then((res) => setUsers(res.data.users || []))
      .catch((err) => console.error('Error fetching users:', err));
    apiService.cases.getAll({ limit: 100 })
      .then((res) => setCases(res.data.cases || []))
      .catch((err) => console.error('Error fetching cases:', err));
  }, []);

  // Related detections load independently so a slow lookup never blocks the main page.
  useEffect(() => {
    if (!detectionId) return undefined;
    let cancelled = false;
    setRelatedLoading(true);
    setRelatedError('');
    apiService.detections.getRelated(detectionId)
      .then((res) => {
        if (cancelled) return;
        setRelatedDetections(res.data.related || []);
        setRelatedTotal(res.data.total || 0);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Error fetching related detections:', err);
        setRelatedError(err.response?.data?.message || 'Failed to load related detections');
      })
      .finally(() => { if (!cancelled) setRelatedLoading(false); });
    return () => { cancelled = true; };
  }, [detectionId]);

  // ---------- helpers ----------
  const formatDetectionDateTime = (dateString) => (dateString ? formatDateTime(dateString, user?.preferences) : '-');
  const severityStyle = (severity) => {
    const color = getSeverityColor(severity) || {};
    return { backgroundColor: color.main || '#757575', color: color.contrastText || '#fff' };
  };
  const statusStyle = (status) => {
    const c = getStatusColor(status);
    const color = (typeof c === 'string' ? c : c?.main) || '#757575';
    return { borderColor: color, color };
  };

  const vendor = detection?.vendor;
  const vendorLabel = vendor ? (VENDOR_LABELS[vendor.type] || vendor.agentName || 'vendor') : '';
  const canPushToVendor = !!(vendor?.agentId && detection?.sourceId && canManageAgents);
  const canIsolate = !!(vendor?.agentId && vendor?.host?.id && canManageAgents);
  const endpointInfo = detection?.enrichmentData?.endpointInfo;
  const isolated = endpointInfo?.isolated === true || String(endpointInfo?.status || '').toLowerCase() === 'isolated';

  // ---------- case management ----------
  const handleCancelCaseManagement = () => {
    setEditingCaseManagement(false);
    setCaseManagementForm(formFromDetection(detection));
  };

  /**
   * Save case-management fields. `overrides` are merged over the current form so
   * quick actions can update a field and save in one step without a stale closure.
   * Only fields that differ from the loaded detection are sent.
   */
  const handleSaveCaseManagement = async (overrides = {}) => {
    if (!detection) return null;
    const form = { ...caseManagementForm, ...overrides };
    const current = formFromDetection(detection);
    const updateData = {};
    ['status', 'severity', 'category'].forEach((key) => {
      if (form[key] && form[key] !== current[key]) updateData[key] = form[key];
    });
    if (form.assignedTo !== current.assignedTo) updateData.assignedTo = form.assignedTo || null;
    if ((form.subcategory || '').trim() !== current.subcategory) updateData.subcategory = (form.subcategory || '').trim() || null;
    if (form.case !== current.case) updateData.case = form.case || null;

    if (Object.keys(updateData).length === 0) {
      setEditingCaseManagement(false);
      return {};
    }

    try {
      setSavingCaseManagement(true);
      await apiService.detections.update(detectionId, updateData);
      await fetchDetection();
      setEditingCaseManagement(false);
      setError('');
      notify('Detection updated');
      return updateData;
    } catch (err) {
      console.error('Error updating detection:', err);
      setError(`Failed to update detection: ${err.response?.data?.message || err.message}`);
      return null;
    } finally {
      setSavingCaseManagement(false);
    }
  };

  const handleCaseManagementFormChange = (field, value) => {
    setCaseManagementForm((prev) => ({ ...prev, [field]: value }));
  };

  const pushVendorStatus = async (status) => {
    if (!canPushToVendor) return;
    try {
      setPushingStatus(true);
      const res = await apiService.agents.updateAlertStatus(vendor.agentId, detection.sourceId, status, `Status set to ${status} in Excubia by ${personName(user, 'analyst')}`);
      if (res.data?.success === false) throw new Error(res.data.message || 'Vendor rejected the status update');
      notify(`Status pushed to ${vendorLabel}`);
    } catch (err) {
      console.error('Error pushing status to vendor:', err);
      notify(`Could not push status to ${vendorLabel}: ${err.response?.data?.message || err.message}`, 'warning');
    } finally {
      setPushingStatus(false);
    }
  };

  const setStatusQuick = async (status, pushToVendor = false) => {
    const result = await handleSaveCaseManagement({ status });
    if (result && pushToVendor) await pushVendorStatus(status);
  };

  const handleCreateCaseFromDetection = async () => {
    try {
      setCreatingCase(true);
      const response = await apiService.detections.createCase({
        title: `Case for Detection: ${detection.title}`.slice(0, 200),
        description: `Case created from detection: ${detection.title}\n\nDetection Description: ${detection.description}`.slice(0, 2000),
        priority: detection.severity,
        detectionIds: [detectionId]
      });
      const newCase = response.data.case;
      setError('');
      notify(`Case ${newCase?.caseNumber || ''} created`);
      if (newCase?._id) {
        setTimeout(() => navigate(`/dashboard/cases/${newCase._id}`), 800);
      } else {
        await fetchDetection();
      }
    } catch (err) {
      console.error('Error creating case:', err);
      setError(err.response?.data?.message || 'Failed to create case from detection');
    } finally {
      setCreatingCase(false);
    }
  };

  // ---------- comments ----------
  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    try {
      setAddingComment(true);
      await apiService.detections.addComment(detectionId, { content: newComment.trim() });
      setNewComment('');
      await fetchDetection();
      setError('');
    } catch (err) {
      console.error('Error adding comment:', err);
      setError(err.response?.data?.message || 'Failed to add comment');
    } finally {
      setAddingComment(false);
    }
  };

  // ---------- tags ----------
  const handleSaveTags = async () => {
    try {
      setSavingTags(true);
      const tags = [...new Set(tagsDraft.map((t) => String(t).trim()).filter(Boolean))];
      await apiService.detections.update(detectionId, { tags });
      await fetchDetection();
      setEditingTags(false);
      notify('Tags updated');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update tags');
    } finally {
      setSavingTags(false);
    }
  };

  // ---------- suppression ----------
  const handleSuppress = async () => {
    try {
      setSavingSuppression(true);
      await apiService.detections.update(detectionId, {
        suppression: {
          ...(detection.suppression || {}),
          isSuppressed: true,
          suppressionReason: suppressForm.reason.trim() || null,
          suppressedUntil: suppressForm.until ? new Date(suppressForm.until).toISOString() : null,
          suppressedBy: user?._id || null,
          suppressedAt: new Date().toISOString()
        }
      });
      await fetchDetection();
      setSuppressDialogOpen(false);
      setSuppressForm({ reason: '', until: '' });
      notify('Detection suppressed');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to suppress detection');
    } finally {
      setSavingSuppression(false);
    }
  };

  const handleUnsuppress = async () => {
    try {
      setSavingSuppression(true);
      await apiService.detections.update(detectionId, {
        suppression: {
          ...(detection.suppression || {}),
          isSuppressed: false,
          suppressionReason: null,
          suppressedUntil: null,
          suppressedBy: null,
          suppressedAt: null
        }
      });
      await fetchDetection();
      notify('Suppression removed');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to unsuppress detection');
    } finally {
      setSavingSuppression(false);
    }
  };

  // ---------- evidence ----------
  const handleAddEvidence = async () => {
    if (!evidenceForm.description.trim() && !evidenceForm.value.trim()) return;
    try {
      setSavingEvidence(true);
      const entry = {
        type: evidenceForm.type,
        description: evidenceForm.description.trim(),
        value: evidenceForm.value.trim() || undefined,
        collectedBy: user?._id || null,
        collectedByName: personName(user, 'Unknown'),
        collectedAt: new Date().toISOString()
      };
      await apiService.detections.update(detectionId, { evidence: [...(detection.evidence || []), entry] });
      await fetchDetection();
      setEvidenceForm({ type: 'Note', description: '', value: '' });
      setShowEvidenceForm(false);
      notify('Evidence added');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add evidence');
    } finally {
      setSavingEvidence(false);
    }
  };

  const handleRemoveEvidence = async (index) => {
    try {
      setSavingEvidence(true);
      const evidence = (detection.evidence || []).filter((_, i) => i !== index);
      await apiService.detections.update(detectionId, { evidence });
      await fetchDetection();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to remove evidence');
    } finally {
      setSavingEvidence(false);
    }
  };

  // ---------- AI / vendor / delete ----------
  const handleRerunAI = async () => {
    try {
      setAnalyzing(true);
      await apiService.ai.analyzeDetection(detectionId);
      await fetchDetection();
      notify('AI analysis updated');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to run AI analysis');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleEnrichDetection = async (refresh = false) => {
    if (!detection) return;
    const previousSeverity = detection.severity;
    try {
      setEnrichingIntel(true);
      const res = await apiService.enrichment.enrichDetection(detectionId, refresh);
      const { result, detection: updatedDetection } = res.data;
      if (updatedDetection) setDetection(updatedDetection);
      const lookupCount = result?.lookups?.length || 0;
      const parts = [`Checked ${lookupCount} indicator${lookupCount === 1 ? '' : 's'}`, `verdict ${result?.verdict || 'unknown'}`];
      if (result?.severity && result.severity !== previousSeverity) {
        parts.push(`severity updated to ${result.severity}`);
      }
      notify(parts.join('; '));
      setError('');
    } catch (err) {
      console.error('Error enriching detection with threat intelligence:', err);
      setError(err.response?.data?.message || 'Failed to enrich detection with threat intelligence');
    } finally {
      setEnrichingIntel(false);
    }
  };

  const handleIsolation = async (action) => {
    try {
      setIsolating(true);
      const fn = action === 'isolate' ? apiService.agents.isolateEndpoint : apiService.agents.unisolateEndpoint;
      const res = await fn(vendor.agentId, vendor.host.id, { detectionId });
      if (res.data?.success === false) throw new Error(res.data.message);
      notify(res.data?.message || (action === 'isolate' ? 'Endpoint isolation requested' : 'Endpoint release requested'));
      await fetchDetection();
    } catch (err) {
      setError(err.response?.data?.message || err.message || `Failed to ${action} endpoint`);
    } finally {
      setIsolating(false);
    }
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      await apiService.detections.delete(detectionId);
      navigate('/dashboard/detections', { replace: true });
    } catch (err) {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setError(err.response?.data?.message || 'Failed to delete detection');
    }
  };

  // ---------- render ----------
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!detection) {
    return (
      <Box p={3}>
        <Alert severity={error ? 'error' : 'warning'} sx={{ mb: 2 }}>{error || 'Detection not found'}</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/dashboard/detections')}>Back to Detections</Button>
      </Box>
    );
  }

  const suppression = detection.suppression || {};
  const indicators = Array.isArray(detection.indicators) ? detection.indicators.filter((i) => i && typeof i === 'object') : [];
  const entities = Array.isArray(detection.entities) ? detection.entities.filter((e) => e && typeof e === 'object') : [];
  const evidence = Array.isArray(detection.evidence) ? detection.evidence : [];
  const enrichment = detection.enrichmentData;
  const hasEnrichment = !!(enrichment && (enrichment.endpointInfo || enrichment.indicators?.length || enrichment.threatIntelMatches?.length || (enrichment.extractedData && Object.keys(enrichment.extractedData).length)));
  const threatIntel = enrichment?.threatIntel;
  const threatIntelVerdictMeta = verdictMeta(threatIntel?.verdict);
  const ai = detection.aiAnalysis;
  const aiPendingStatus = ai?.status === 'pending' || ai?.status === 'failed' ? ai.status : null;
  const isOpen = ['New', 'In Progress', 'Under Review'].includes(detection.status);

  return (
    <Box p={3}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link component={RouterLink} to="/dashboard" underline="hover" color="inherit" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <HomeIcon fontSize="small" /> Dashboard
        </Link>
        <Link component={RouterLink} to="/dashboard/detections" underline="hover" color="inherit" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <DetectionIcon fontSize="small" /> Detections
        </Link>
        <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }} noWrap>
          {detection.title}
        </Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3} flexWrap="wrap" gap={2}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h4" gutterBottom sx={{ wordBreak: 'break-word' }}>{detection.title}</Typography>
          <Box display="flex" gap={1} mb={1} flexWrap="wrap">
            <Chip label={detection.severity} size="small" sx={severityStyle(detection.severity)} />
            <Chip label={detection.status} size="small" variant="outlined" sx={statusStyle(detection.status)} />
            <Chip label={detection.category} variant="outlined" size="small" />
            {detection.subcategory && <Chip label={detection.subcategory} variant="outlined" size="small" />}
            {suppression.isSuppressed && <Chip icon={<SuppressIcon />} label="Suppressed" size="small" color="default" />}
            {vendor?.agentName && <Chip icon={<HostIcon />} label={vendor.agentName} size="small" variant="outlined" />}
            {isolated && <Chip icon={<IsolateIcon />} label="Endpoint isolated" size="small" color="warning" />}
          </Box>
        </Box>
        <Box display="flex" gap={1} flexWrap="wrap">
          {canDelete && (
            <Button startIcon={<DeleteIcon />} color="error" variant="outlined" onClick={() => setDeleteDialogOpen(true)}>
              Delete
            </Button>
          )}
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/dashboard/detections')} variant="outlined">
            Back to Detections
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>{error}</Alert>
      )}

      {suppression.isSuppressed && (
        <Alert
          severity="info"
          icon={<SuppressIcon />}
          sx={{ mb: 3 }}
          action={canUpdate && (
            <Button color="inherit" size="small" startIcon={<UnsuppressIcon />} onClick={handleUnsuppress} disabled={savingSuppression}>
              Unsuppress
            </Button>
          )}
        >
          This detection is suppressed{suppression.suppressionReason ? `: ${suppression.suppressionReason}` : ''}
          {suppression.suppressedUntil ? ` (until ${formatDetectionDateTime(suppression.suppressedUntil)})` : ''}.
        </Alert>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} aria-label="detection detail tabs">
          <Tab label="Overview" />
          <Tab label="Raw Data" />
        </Tabs>
      </Paper>

      {activeTab === 0 && (
        <Grid container spacing={3}>
          {/* Main Content */}
          <Grid item xs={12} lg={8}>
            {/* Basic Information */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Basic Information</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>Detection ID</Typography>
                    <Typography variant="body1" gutterBottom sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{detection._id}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>Detection Time</Typography>
                    <Typography variant="body1" gutterBottom>{formatDetectionDateTime(detection.detectionTime)}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>Created</Typography>
                    <Typography variant="body1" gutterBottom>
                      {formatDetectionDateTime(detection.createdAt)}
                      {detection.createdBy && ` by ${personName(detection.createdBy)}`}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>Last Updated</Typography>
                    <Typography variant="body1" gutterBottom>
                      {formatDetectionDateTime(detection.updatedAt)}
                      {detection.lastModifiedBy && ` by ${personName(detection.lastModifiedBy)}`}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>Source</Typography>
                    <Typography variant="body1" gutterBottom>{detection.source || 'Unknown'}</Typography>
                  </Grid>
                  {detection.sourceId && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>Source ID</Typography>
                      <Typography variant="body1" gutterBottom sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{detection.sourceId}</Typography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>

            {/* Description */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Description</Typography>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>{detection.description}</Typography>
              </CardContent>
            </Card>

            {/* Case Management */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6" display="flex" alignItems="center" gap={1}>
                    <AssignmentIcon color="primary" /> Case Management
                  </Typography>
                  {canUpdate && (!editingCaseManagement ? (
                    <Button startIcon={<EditIcon />} onClick={() => setEditingCaseManagement(true)} size="small" variant="outlined">Edit</Button>
                  ) : (
                    <Box display="flex" gap={1}>
                      <Button startIcon={<SaveIcon />} onClick={() => handleSaveCaseManagement()} size="small" variant="contained" disabled={savingCaseManagement}>
                        {savingCaseManagement ? 'Saving...' : 'Save'}
                      </Button>
                      <Button startIcon={<CancelIcon />} onClick={handleCancelCaseManagement} size="small" variant="outlined" disabled={savingCaseManagement}>
                        Cancel
                      </Button>
                    </Box>
                  ))}
                </Box>

                {!editingCaseManagement ? (
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" gutterBottom><strong>Status:</strong></Typography>
                      <Chip label={detection.status} size="small" variant="outlined" sx={{ mb: 2, ...statusStyle(detection.status) }} />
                      <Typography variant="body2" gutterBottom><strong>Severity:</strong></Typography>
                      <Chip label={detection.severity} size="small" sx={{ mb: 2, ...severityStyle(detection.severity) }} />
                      <Typography variant="body2" gutterBottom><strong>Category:</strong></Typography>
                      <Chip label={detection.category} variant="outlined" size="small" sx={{ mb: 2 }} />
                      {detection.subcategory && (
                        <>
                          <Typography variant="body2" gutterBottom><strong>Subcategory:</strong></Typography>
                          <Chip label={detection.subcategory} variant="outlined" size="small" sx={{ mb: 2 }} />
                        </>
                      )}
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" gutterBottom><strong>Assigned To:</strong></Typography>
                      <Box display="flex" alignItems="center" gap={1} mb={2}>
                        <PersonIcon fontSize="small" />
                        <Typography variant="body2">{detection.assignedTo ? personName(detection.assignedTo, 'Unassigned') : 'Unassigned'}</Typography>
                      </Box>
                      <Typography variant="body2" gutterBottom><strong>Associated Case:</strong></Typography>
                      <Box display="flex" alignItems="center" gap={1} mb={2}>
                        <CaseIcon fontSize="small" />
                        {detection.case ? (
                          <Link component={RouterLink} to={`/dashboard/cases/${detection.case._id || detection.case}`} underline="hover" variant="body2">
                            {detection.case.caseNumber ? `${detection.case.caseNumber} - ${detection.case.title}` : 'View case'}
                          </Link>
                        ) : (
                          <Typography variant="body2">No case assigned</Typography>
                        )}
                      </Box>
                      {!detection.case && canCreateCase && (
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={creatingCase ? <CircularProgress size={14} /> : <CaseIcon />}
                          onClick={handleCreateCaseFromDetection}
                          disabled={creatingCase}
                          sx={{ mt: 1 }}
                        >
                          {creatingCase ? 'Creating...' : 'Create Case'}
                        </Button>
                      )}
                    </Grid>
                  </Grid>
                ) : (
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth margin="normal" size="small">
                        <InputLabel>Status</InputLabel>
                        <Select value={caseManagementForm.status} onChange={(e) => handleCaseManagementFormChange('status', e.target.value)} label="Status">
                          {options.statuses.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                        </Select>
                      </FormControl>
                      <FormControl fullWidth margin="normal" size="small">
                        <InputLabel>Severity</InputLabel>
                        <Select value={caseManagementForm.severity} onChange={(e) => handleCaseManagementFormChange('severity', e.target.value)} label="Severity">
                          {options.severities.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                        </Select>
                      </FormControl>
                      <FormControl fullWidth margin="normal" size="small">
                        <InputLabel>Category</InputLabel>
                        <Select value={caseManagementForm.category} onChange={(e) => handleCaseManagementFormChange('category', e.target.value)} label="Category">
                          {(options.categories.includes(caseManagementForm.category) || !caseManagementForm.category
                            ? options.categories
                            : [caseManagementForm.category, ...options.categories]
                          ).map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                        </Select>
                      </FormControl>
                      <TextField
                        fullWidth
                        margin="normal"
                        size="small"
                        label="Subcategory"
                        value={caseManagementForm.subcategory}
                        onChange={(e) => handleCaseManagementFormChange('subcategory', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Autocomplete
                        options={users}
                        getOptionLabel={(option) => `${option.firstName} ${option.lastName}${option.username ? ` (${option.username})` : ''}`}
                        isOptionEqualToValue={(a, b) => a._id === b._id}
                        value={users.find((u) => u._id === caseManagementForm.assignedTo) || null}
                        onChange={(event, newValue) => handleCaseManagementFormChange('assignedTo', newValue?._id || '')}
                        renderInput={(params) => <TextField {...params} label="Assigned To" margin="normal" size="small" fullWidth />}
                        renderOption={(props, option) => (
                          <Box component="li" {...props} key={option._id}>
                            <Avatar sx={{ mr: 2, width: 24, height: 24 }}>{option.firstName?.[0]}{option.lastName?.[0]}</Avatar>
                            {option.firstName} {option.lastName}{option.username ? ` (${option.username})` : ''}
                          </Box>
                        )}
                      />
                      <Autocomplete
                        options={cases}
                        getOptionLabel={(option) => `${option.caseNumber} - ${option.title}`}
                        isOptionEqualToValue={(a, b) => a._id === b._id}
                        value={cases.find((c) => c._id === caseManagementForm.case) || null}
                        onChange={(event, newValue) => handleCaseManagementFormChange('case', newValue?._id || '')}
                        renderInput={(params) => <TextField {...params} label="Associated Case" margin="normal" size="small" fullWidth />}
                        renderOption={(props, option) => (
                          <Box component="li" {...props} key={option._id}>
                            <CaseIcon sx={{ mr: 2 }} /> {option.caseNumber} - {option.title}
                          </Box>
                        )}
                      />
                    </Grid>
                  </Grid>
                )}
              </CardContent>
            </Card>

            {/* Vendor / EDR details */}
            {vendor && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
                    <Typography variant="h6" display="flex" alignItems="center" gap={1}>
                      <HostIcon color="primary" /> {vendorLabel} Alert
                    </Typography>
                    <Box display="flex" gap={1} flexWrap="wrap">
                      {vendor.link && (
                        <Button size="small" variant="outlined" endIcon={<OpenInNewIcon />} href={vendor.link} target="_blank" rel="noopener noreferrer">
                          Open in {vendorLabel}
                        </Button>
                      )}
                      {canPushToVendor && (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={pushingStatus ? <CircularProgress size={14} /> : <PushIcon />}
                          onClick={() => pushVendorStatus(detection.status)}
                          disabled={pushingStatus}
                        >
                          Push status to {vendorLabel}
                        </Button>
                      )}
                    </Box>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Field label="Integration" value={vendor.agentName} />
                      <Field label="Vendor status" value={vendor.status} />
                      <Field label="Vendor severity" value={vendor.severity} />
                      <Field label="Vendor category" value={vendor.category} />
                      <Field label="User" value={vendor.user} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      {vendor.host && (
                        <>
                          <Typography variant="subtitle2" gutterBottom>Host</Typography>
                          <Field label="Hostname" value={vendor.host.hostname} />
                          <Field label="IP" value={vendor.host.ip} />
                          <Field label="External IP" value={vendor.host.externalIp} />
                          <Field label="OS" value={vendor.host.os} />
                          <Field label="Host ID" value={vendor.host.id} mono />
                        </>
                      )}
                    </Grid>
                    {vendor.process && (vendor.process.name || vendor.process.path || vendor.process.commandLine || vendor.process.sha256) && (
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" gutterBottom>Process</Typography>
                        <Field label="Name" value={vendor.process.name} />
                        <Field label="Path" value={vendor.process.path} mono />
                        <Field label="Command line" value={vendor.process.commandLine} mono />
                        <Field label="SHA256" value={vendor.process.sha256} mono />
                      </Grid>
                    )}
                    {vendor.mitre && (vendor.mitre.tactics?.length || vendor.mitre.techniques?.length) ? (
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" gutterBottom>MITRE ATT&CK</Typography>
                        <Box display="flex" gap={1} flexWrap="wrap">
                          {(vendor.mitre.tactics || []).map((t, i) => <Chip key={`t-${i}`} label={t} size="small" color="primary" variant="outlined" />)}
                          {(vendor.mitre.techniques || []).map((t, i) => <Chip key={`q-${i}`} label={t} size="small" variant="outlined" />)}
                        </Box>
                      </Grid>
                    ) : null}
                  </Grid>
                </CardContent>
              </Card>
            )}

            {/* AI Analysis */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={ai ? 2 : 0} flexWrap="wrap" gap={1}>
                  <Typography variant="h6" display="flex" alignItems="center" gap={1}>
                    <SecurityIcon color="primary" /> AI Analysis
                    {ai && aiPendingStatus === 'pending' && (
                      <Tooltip title={`${ai.pendingMessage || 'Waiting for AI capacity.'}${ai.queuedAt ? ` (queued ${formatDetectionDateTime(ai.queuedAt)})` : ''}`}>
                        <Chip label="Queued" size="small" color="warning" sx={{ ml: 1 }} />
                      </Tooltip>
                    )}
                    {ai && aiPendingStatus === 'failed' && (
                      <Tooltip title={ai.error || 'AI analysis failed.'}>
                        <Chip label="Failed" size="small" color="error" sx={{ ml: 1 }} />
                      </Tooltip>
                    )}
                    {ai && !aiPendingStatus && <Chip label={`${ai.confidence || 0}% confidence`} size="small" color="info" sx={{ ml: 1 }} />}
                  </Typography>
                  {canUpdate && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={analyzing ? <CircularProgress size={14} /> : <AIIcon />}
                      onClick={handleRerunAI}
                      disabled={analyzing}
                    >
                      {analyzing ? 'Analyzing...' : 'Run AI analysis now'}
                    </Button>
                  )}
                </Box>

                {!ai && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    This detection has not been analyzed yet.
                  </Typography>
                )}

                {ai && aiPendingStatus === 'pending' && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    AI analysis is queued and will run automatically when capacity is available.
                    {ai.pendingReason === 'AI_CREDITS' && ' Google AI prepaid credits are depleted — a platform administrator needs to top up billing.'}
                  </Alert>
                )}

                {ai && aiPendingStatus === 'failed' && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    AI analysis failed{ai.error ? `: ${ai.error}` : '.'}
                  </Alert>
                )}

                {ai && !aiPendingStatus && (
                  <>
                    <Accordion defaultExpanded>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography variant="subtitle1">AI Summary</Typography></AccordionSummary>
                      <AccordionDetails>
                        <Typography variant="body2" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>{ai.summary || 'No summary available'}</Typography>
                        {ai.htmlSummary && (
                          <Box
                            dangerouslySetInnerHTML={{ __html: ai.htmlSummary }}
                            sx={{ '& h3': { fontSize: '1.1rem', fontWeight: 'bold', mb: 1 }, '& p': { mb: 1 }, '& ul, & ol': { pl: 2, mb: 1 }, '& li': { mb: 0.5 } }}
                          />
                        )}
                      </AccordionDetails>
                    </Accordion>

                    {ai.threatAssessment && (
                      <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography variant="subtitle1">Threat Assessment</Typography></AccordionSummary>
                        <AccordionDetails>
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                              <Typography variant="body2" paragraph><strong>Threat Level:</strong> {ai.threatAssessment.threatLevel || 'Unknown'}</Typography>
                              <Typography variant="body2" paragraph><strong>Impact Assessment:</strong> {ai.threatAssessment.impactAssessment || 'Not assessed'}</Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              {ai.threatAssessment.attackTechniques?.length > 0 && (
                                <>
                                  <Typography variant="body2" gutterBottom><strong>Attack Techniques:</strong></Typography>
                                  <Box sx={{ mb: 2 }}>
                                    {ai.threatAssessment.attackTechniques.map((t, i) => <Chip key={i} label={t} variant="outlined" size="small" sx={{ mr: 1, mb: 1 }} />)}
                                  </Box>
                                </>
                              )}
                              {ai.threatAssessment.riskFactors?.length > 0 && (
                                <>
                                  <Typography variant="body2" gutterBottom><strong>Risk Factors:</strong></Typography>
                                  <Box>
                                    {ai.threatAssessment.riskFactors.map((f, i) => <Chip key={i} label={f} color="warning" variant="outlined" size="small" sx={{ mr: 1, mb: 1 }} />)}
                                  </Box>
                                </>
                              )}
                            </Grid>
                          </Grid>
                        </AccordionDetails>
                      </Accordion>
                    )}

                    {ai.extractedIndicators?.length > 0 && (
                      <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Typography variant="subtitle1">Extracted Indicators ({ai.extractedIndicators.length})</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          {ai.extractedIndicators.map((indicator, index) => (
                            <Box key={index} mb={1}>
                              <Chip label={`${indicator.type}: ${indicator.value}`} variant="outlined" size="small" sx={{ mr: 1, mb: 1 }} />
                              {indicator.confidence && <Chip label={`${indicator.confidence}% confidence`} color="info" size="small" sx={{ mr: 1, mb: 1 }} />}
                            </Box>
                          ))}
                        </AccordionDetails>
                      </Accordion>
                    )}

                    {ai.recommendedActions?.length > 0 && (
                      <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Typography variant="subtitle1">Recommended Actions ({ai.recommendedActions.length})</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <List dense>
                            {ai.recommendedActions.map((a, i) => (
                              <ListItem key={i}>
                                <ListItemText
                                  primary={typeof a === 'string' ? a : a.action}
                                  secondary={typeof a === 'object' && (a.priority || a.confidence) ? [a.priority && `Priority: ${a.priority}`, a.confidence && `Confidence: ${a.confidence}%`].filter(Boolean).join(' | ') : null}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </AccordionDetails>
                      </Accordion>
                    )}

                    {ai.relatedThreatIntelligence && (ai.relatedThreatIntelligence.similarThreats?.length || ai.relatedThreatIntelligence.iocMatches?.length) ? (
                      <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography variant="subtitle1">Related Threat Intelligence</Typography></AccordionSummary>
                        <AccordionDetails>
                          {ai.relatedThreatIntelligence.similarThreats?.length > 0 && (
                            <Box mb={2}>
                              <Typography variant="body2" gutterBottom><strong>Similar Threats:</strong></Typography>
                              {ai.relatedThreatIntelligence.similarThreats.map((t, i) => <Chip key={i} label={t} color="error" variant="outlined" size="small" sx={{ mr: 1, mb: 1 }} />)}
                            </Box>
                          )}
                          {ai.relatedThreatIntelligence.iocMatches?.length > 0 && (
                            <Box>
                              <Typography variant="body2" gutterBottom><strong>IOC Matches:</strong></Typography>
                              {ai.relatedThreatIntelligence.iocMatches.map((ioc, i) => <Chip key={i} label={ioc} color="warning" size="small" sx={{ mr: 1, mb: 1 }} />)}
                            </Box>
                          )}
                        </AccordionDetails>
                      </Accordion>
                    ) : null}

                    <Box mt={2} p={2} bgcolor="grey.50" borderRadius={1}>
                      <Typography variant="caption" color="text.secondary">
                        Processed by {ai.model || 'AI Model'} on {ai.processedAt ? formatDetectionDateTime(ai.processedAt) : 'unknown date'}
                      </Typography>
                    </Box>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Threat Intelligence */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2} flexWrap="wrap" gap={1}>
                  <Box>
                    <Typography variant="h6" display="flex" alignItems="center" gap={1}>
                      <ThreatIntelIcon color="primary" /> Threat Intelligence
                      {threatIntel?.verdict && (
                        <Chip size="small" color={threatIntelVerdictMeta.color} label={threatIntelVerdictMeta.label} sx={{ ml: 1 }} />
                      )}
                    </Typography>
                    {threatIntel?.enrichedAt && (
                      <Tooltip title={formatDetectionDateTime(threatIntel.enrichedAt)}>
                        <Typography variant="caption" color="text.secondary">
                          Checked {formatDistanceToNow(new Date(threatIntel.enrichedAt), { addSuffix: true })}
                        </Typography>
                      </Tooltip>
                    )}
                  </Box>
                  {canUpdate && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={enrichingIntel ? <CircularProgress size={14} /> : (threatIntel ? <RefreshIcon /> : <ThreatIntelIcon />)}
                      onClick={() => handleEnrichDetection(!!threatIntel)}
                      disabled={enrichingIntel}
                    >
                      {enrichingIntel ? 'Checking...' : threatIntel ? 'Refresh' : 'Enrich now'}
                    </Button>
                  )}
                </Box>

                {!threatIntel && (
                  <Typography variant="body2" color="text.secondary">
                    Enrichment queries the threat-intel providers configured under{' '}
                    <Link component={RouterLink} to="/dashboard/integrations">Integrations</Link>
                    {' '}(e.g. VirusTotal, AbuseIPDB) for the indicators linked to this detection.
                  </Typography>
                )}

                {threatIntel && (
                  <>
                    {threatIntel.aiSummary && (
                      <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                        <Typography variant="subtitle2" gutterBottom>AI summary</Typography>
                        {renderMarkdown(threatIntel.aiSummary)}
                      </Box>
                    )}
                    {(threatIntel.lookups || []).length === 0 && (
                      <Typography variant="body2" color="text.secondary">No indicators were available to check.</Typography>
                    )}
                    {(threatIntel.lookups || []).map((lookup, index) => (
                      <React.Fragment key={`${lookup.type}-${lookup.value}-${index}`}>
                        {index > 0 && <Divider sx={{ my: 2 }} />}
                        <IntelResultList lookup={lookup} dense />
                      </React.Fragment>
                    ))}
                  </>
                )}
              </CardContent>
            </Card>

            {/* Related Detections */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <RelatedIcon color="primary" />
                  <Typography variant="h6">Related Detections</Typography>
                  {relatedTotal > 0 && <Chip size="small" variant="outlined" label={relatedTotal} />}
                </Box>

                {relatedLoading ? (
                  <Box display="flex" justifyContent="center" py={2}>
                    <CircularProgress size={24} />
                  </Box>
                ) : relatedError ? (
                  <Alert severity="error">{relatedError}</Alert>
                ) : relatedDetections.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No related detections in the last 14 days
                  </Typography>
                ) : (
                  <List dense disablePadding>
                    {relatedDetections.slice(0, 10).map((r, index) => (
                      <React.Fragment key={r._id}>
                        {index > 0 && <Divider component="li" sx={{ my: 1 }} />}
                        <ListItem disableGutters sx={{ display: 'block' }}>
                          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                            <Chip size="small" label={r.severity || 'Unknown'} sx={severityStyle(r.severity)} />
                            <Link component={RouterLink} to={`/dashboard/detections/${r._id}`} underline="hover" sx={{ fontWeight: 500 }}>
                              {r.title || r._id}
                            </Link>
                            {r.case && <Chip size="small" color="primary" variant="outlined" label="in case" />}
                          </Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            {[r.host, r.createdAt ? formatRelativeTime(r.createdAt) : null].filter(Boolean).join(' · ')}
                          </Typography>
                          {(r.reasons || []).length > 0 && (
                            <Box display="flex" gap={0.5} flexWrap="wrap" mt={0.5}>
                              {r.reasons.map((reason, i) => (
                                <Chip key={i} size="small" variant="outlined" label={reason} />
                              ))}
                            </Box>
                          )}
                        </ListItem>
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>

            {/* Enrichment Data */}
            {hasEnrichment && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Enrichment Data</Typography>

                  {enrichment.endpointInfo && (
                    <Accordion defaultExpanded>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography variant="subtitle1">Endpoint Information</Typography></AccordionSummary>
                      <AccordionDetails>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <Field label="Hostname" value={enrichment.endpointInfo.hostname} />
                            <Field label="IP Address" value={enrichment.endpointInfo.ip || enrichment.endpointInfo.ipAddress} />
                            <Field label="Operating System" value={enrichment.endpointInfo.os || enrichment.endpointInfo.operatingSystem} />
                            <Field label="Status" value={enrichment.endpointInfo.status} />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <Field label="Isolated" value={enrichment.endpointInfo.isolated === undefined ? undefined : (enrichment.endpointInfo.isolated ? 'Yes' : 'No')} />
                            <Field label="Risk Score" value={enrichment.endpointInfo.riskScore} />
                            <Field label="Exposure Level" value={enrichment.endpointInfo.exposureLevel} />
                            <Field label="Last Seen" value={enrichment.endpointInfo.lastSeen ? formatDetectionDateTime(enrichment.endpointInfo.lastSeen) : undefined} />
                          </Grid>
                          {enrichment.endpointInfo.tags?.length > 0 && (
                            <Grid item xs={12}>
                              <Typography variant="body2" gutterBottom><strong>Tags:</strong></Typography>
                              {enrichment.endpointInfo.tags.map((t, i) => <Chip key={i} label={t} size="small" variant="outlined" sx={{ mr: 1, mb: 1 }} />)}
                            </Grid>
                          )}
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                  )}

                  {enrichment.indicators?.length > 0 && (
                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle1">Observed Indicators ({enrichment.indicators.length})</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        {enrichment.indicators.map((indicator, index) => (
                          <Chip key={index} label={`${indicator.type}: ${indicator.value}`} variant="outlined" size="small" sx={{ mr: 1, mb: 1 }} />
                        ))}
                      </AccordionDetails>
                    </Accordion>
                  )}

                  {enrichment.threatIntelMatches?.length > 0 && (
                    <Accordion defaultExpanded>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle1">Threat Intelligence Matches ({enrichment.threatIntelMatches.length})</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        {enrichment.threatIntelMatches.map((match, index) => (
                          <Box key={index} mb={2} p={2} bgcolor="grey.50" borderRadius={1}>
                            <Box display="flex" gap={1} mb={1} flexWrap="wrap">
                              {match.severity && <Chip label={match.severity} size="small" sx={severityStyle(match.severity)} />}
                              {match.threatType && <Chip label={match.threatType} size="small" variant="outlined" />}
                              {match.confidence !== undefined && <Chip label={`${match.confidence}% confidence`} size="small" color="info" />}
                            </Box>
                            <Field label="Indicator" value={match.indicator} mono />
                            <Field label="Source" value={match.source} />
                            <Field label="Description" value={match.description} />
                          </Box>
                        ))}
                      </AccordionDetails>
                    </Accordion>
                  )}

                  {enrichment.extractedData && Object.keys(enrichment.extractedData).length > 0 && (
                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography variant="subtitle1">Additional Data</Typography></AccordionSummary>
                      <AccordionDetails>
                        <Box component="pre" sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1, overflow: 'auto', fontSize: '0.875rem', whiteSpace: 'pre-wrap', m: 0 }}>
                          {JSON.stringify(enrichment.extractedData, null, 2)}
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Linked indicators & entities */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
                      <IndicatorIcon color="primary" /> Indicators ({indicators.length})
                    </Typography>
                    {indicators.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">No indicators linked.</Typography>
                    ) : (
                      <List dense disablePadding>
                        {indicators.map((ind) => (
                          <ListItem key={ind._id} disableGutters divider>
                            <ListItemText
                              primary={
                                <Link component={RouterLink} to={`/dashboard/indicators/${ind._id}`} underline="hover" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                  {ind.value || ind._id}
                                </Link>
                              }
                              secondaryTypographyProps={{ component: 'div' }}
                              secondary={
                                <Box display="flex" gap={0.5} mt={0.5} flexWrap="wrap">
                                  {ind.type && <Chip label={ind.type} size="small" variant="outlined" />}
                                  {ind.severity && <Chip label={ind.severity} size="small" sx={severityStyle(ind.severity)} />}
                                  {ind.status && <Chip label={ind.status} size="small" variant="outlined" />}
                                </Box>
                              }
                            />
                          </ListItem>
                        ))}
                      </List>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
                      <EntityIcon color="primary" /> Entities ({entities.length})
                    </Typography>
                    {entities.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">No entities linked.</Typography>
                    ) : (
                      <List dense disablePadding>
                        {entities.map((ent) => (
                          <ListItem key={ent._id} disableGutters divider>
                            <ListItemText
                              primary={
                                <Link component={RouterLink} to={`/dashboard/entities/${ent._id}`} underline="hover">
                                  {ent.name || ent.identifier || ent._id}
                                </Link>
                              }
                              secondaryTypographyProps={{ component: 'div' }}
                              secondary={
                                <Box display="flex" gap={0.5} mt={0.5} flexWrap="wrap" alignItems="center">
                                  {ent.type && <Chip label={ent.type} size="small" variant="outlined" />}
                                  {ent.status && <Chip label={ent.status} size="small" variant="outlined" />}
                                  {ent.identifier && ent.name && <Typography variant="caption" color="text.secondary">{ent.identifier}</Typography>}
                                </Box>
                              }
                            />
                          </ListItem>
                        ))}
                      </List>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Evidence */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="h6" display="flex" alignItems="center" gap={1}>
                    <EvidenceIcon color="primary" /> Evidence ({evidence.length})
                  </Typography>
                  {canUpdate && !showEvidenceForm && (
                    <Button size="small" startIcon={<AddIcon />} onClick={() => setShowEvidenceForm(true)}>Add evidence</Button>
                  )}
                </Box>

                {evidence.length === 0 && !showEvidenceForm && (
                  <Typography variant="body2" color="text.secondary">No evidence recorded.</Typography>
                )}

                {evidence.length > 0 && (
                  <List dense disablePadding>
                    {evidence.map((item, index) => (
                      <ListItem
                        key={index}
                        disableGutters
                        divider
                        secondaryAction={canUpdate && (
                          <Tooltip title="Remove">
                            <span>
                              <IconButton edge="end" size="small" onClick={() => handleRemoveEvidence(index)} disabled={savingEvidence}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      >
                        <ListItemText
                          primary={
                            <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                              {item.type && <Chip label={item.type} size="small" variant="outlined" />}
                              <Typography variant="body2">{item.description || item.name || item.title}</Typography>
                            </Box>
                          }
                          secondaryTypographyProps={{ component: 'div' }}
                          secondary={
                            <Box mt={0.5}>
                              {(item.value || item.content || item.url) && (
                                <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all', whiteSpace: 'pre-wrap' }}>
                                  {item.value || item.content || item.url}
                                </Typography>
                              )}
                              <Typography variant="caption" color="text.secondary">
                                {item.collectedAt ? formatDetectionDateTime(item.collectedAt) : ''}
                                {item.collectedBy && typeof item.collectedBy === 'object' ? ` by ${personName(item.collectedBy)}` : item.collectedByName ? ` by ${item.collectedByName}` : ''}
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                )}

                {showEvidenceForm && (
                  <Box mt={2} display="flex" flexDirection="column" gap={2}>
                    <Box display="flex" gap={2} flexWrap="wrap">
                      <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel>Type</InputLabel>
                        <Select value={evidenceForm.type} label="Type" onChange={(e) => setEvidenceForm((f) => ({ ...f, type: e.target.value }))}>
                          {EVIDENCE_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                        </Select>
                      </FormControl>
                      <TextField
                        size="small"
                        label="Description"
                        value={evidenceForm.description}
                        onChange={(e) => setEvidenceForm((f) => ({ ...f, description: e.target.value }))}
                        sx={{ flex: 1, minWidth: 200 }}
                      />
                    </Box>
                    <TextField
                      size="small"
                      label="Value / content (hash, URL, log excerpt...)"
                      value={evidenceForm.value}
                      onChange={(e) => setEvidenceForm((f) => ({ ...f, value: e.target.value }))}
                      multiline
                      minRows={2}
                      fullWidth
                    />
                    <Box display="flex" gap={1} justifyContent="flex-end">
                      <Button size="small" onClick={() => { setShowEvidenceForm(false); setEvidenceForm({ type: 'Note', description: '', value: '' }); }} disabled={savingEvidence}>Cancel</Button>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={handleAddEvidence}
                        disabled={savingEvidence || (!evidenceForm.description.trim() && !evidenceForm.value.trim())}
                      >
                        {savingEvidence ? 'Saving...' : 'Add'}
                      </Button>
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Sidebar */}
          <Grid item xs={12} lg={4}>
            {/* Quick Actions */}
            {(canUpdate || canIsolate) && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Quick Actions</Typography>
                  <Box display="flex" flexDirection="column" gap={1}>
                    {canUpdate && detection.status === 'New' && (
                      <Button variant="contained" color="primary" onClick={() => setStatusQuick('In Progress')} disabled={savingCaseManagement} fullWidth>
                        Start Investigation
                      </Button>
                    )}
                    {canUpdate && isOpen && detection.status !== 'New' && (
                      <Button variant="contained" color="success" onClick={() => setStatusQuick('Resolved')} disabled={savingCaseManagement} fullWidth>
                        Mark Resolved
                      </Button>
                    )}
                    {canUpdate && isOpen && (
                      <Button variant="outlined" color="warning" onClick={() => setStatusQuick('False Positive')} disabled={savingCaseManagement} fullWidth>
                        Mark False Positive
                      </Button>
                    )}
                    {canUpdate && !isOpen && (
                      <Button variant="outlined" onClick={() => setStatusQuick('In Progress')} disabled={savingCaseManagement} fullWidth>
                        Reopen
                      </Button>
                    )}
                    {canUpdate && user?._id && detection.assignedTo?._id !== user._id && (
                      <Button variant="outlined" startIcon={<PersonIcon />} onClick={() => handleSaveCaseManagement({ assignedTo: user._id })} disabled={savingCaseManagement} fullWidth>
                        Assign to Me
                      </Button>
                    )}
                    {canUpdate && (suppression.isSuppressed ? (
                      <Button variant="outlined" startIcon={<UnsuppressIcon />} onClick={handleUnsuppress} disabled={savingSuppression} fullWidth>
                        Unsuppress
                      </Button>
                    ) : (
                      <Button variant="outlined" color="inherit" startIcon={<SuppressIcon />} onClick={() => setSuppressDialogOpen(true)} disabled={savingSuppression} fullWidth>
                        Suppress
                      </Button>
                    ))}
                    {canIsolate && (
                      <>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="caption" color="text.secondary">
                          Endpoint {vendor.host.hostname || vendor.host.id} via {vendorLabel}
                        </Typography>
                        {isolated ? (
                          <Button variant="outlined" color="success" startIcon={isolating ? <CircularProgress size={14} /> : <ReleaseIcon />} onClick={() => handleIsolation('unisolate')} disabled={isolating} fullWidth>
                            Release endpoint
                          </Button>
                        ) : (
                          <Button variant="contained" color="error" startIcon={isolating ? <CircularProgress size={14} color="inherit" /> : <IsolateIcon />} onClick={() => handleIsolation('isolate')} disabled={isolating} fullWidth>
                            Isolate endpoint
                          </Button>
                        )}
                      </>
                    )}
                    {canPushToVendor && (
                      <Typography variant="caption" color="text.secondary">
                        Status changes are pushed to {vendorLabel}.
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* Tags */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="h6" display="flex" alignItems="center" gap={1}>
                    <TagIcon color="primary" /> Tags
                  </Typography>
                  {canUpdate && !editingTags && (
                    <Button size="small" startIcon={<EditIcon />} onClick={() => { setTagsDraft(detection.tags || []); setEditingTags(true); }}>Edit</Button>
                  )}
                </Box>
                {editingTags ? (
                  <Box display="flex" flexDirection="column" gap={1}>
                    <Autocomplete
                      multiple
                      freeSolo
                      options={[]}
                      value={tagsDraft}
                      onChange={(e, value) => setTagsDraft(value)}
                      renderTags={(value, getTagProps) => value.map((option, index) => (
                        <Chip label={option} size="small" {...getTagProps({ index })} key={`${option}-${index}`} />
                      ))}
                      renderInput={(params) => <TextField {...params} size="small" placeholder="Type a tag and press Enter" />}
                    />
                    <Box display="flex" gap={1} justifyContent="flex-end">
                      <Button size="small" onClick={() => setEditingTags(false)} disabled={savingTags}>Cancel</Button>
                      <Button size="small" variant="contained" onClick={handleSaveTags} disabled={savingTags}>{savingTags ? 'Saving...' : 'Save'}</Button>
                    </Box>
                  </Box>
                ) : (
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {(detection.tags || []).length === 0 ? (
                      <Typography variant="body2" color="text.secondary">No tags</Typography>
                    ) : detection.tags.map((tag, i) => <Chip key={`${tag}-${i}`} label={tag} size="small" variant="outlined" />)}
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* Timeline */}
            {detection.timeline?.length > 0 && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
                    <TimelineIcon color="primary" /> Timeline
                  </Typography>
                  <List dense>
                    {[...detection.timeline].reverse().map((entry, index) => {
                      const who = entry.isSystemGenerated || !entry.performedBy ? 'System' : personName(entry.performedBy);
                      return (
                        <ListItem key={index} alignItems="flex-start" disableGutters>
                          <ListItemAvatar>
                            <Avatar sx={{ width: 32, height: 32, bgcolor: who === 'System' ? 'grey.500' : 'primary.main' }}>
                              {who === 'System' ? <AIIcon fontSize="small" /> : who[0]}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={entry.action}
                            secondaryTypographyProps={{ component: 'div' }}
                            secondary={
                              <Box>
                                {entry.description && (
                                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{entry.description}</Typography>
                                )}
                                <Typography variant="caption" color="text.secondary">
                                  {formatDetectionDateTime(entry.timestamp)} by {who}
                                </Typography>
                              </Box>
                            }
                          />
                        </ListItem>
                      );
                    })}
                  </List>
                </CardContent>
              </Card>
            )}

            {/* Comments */}
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
                  <CommentIcon color="primary" /> Comments ({detection.comments?.length || 0})
                </Typography>

                {detection.comments?.length > 0 ? (
                  <List sx={{ mb: 2 }}>
                    {detection.comments.map((comment, index) => (
                      <ListItem key={index} alignItems="flex-start" disableGutters>
                        <ListItemAvatar>
                          <Avatar sx={{ width: 32, height: 32 }}>{comment.author?.firstName?.[0] || 'S'}</Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
                              <Typography variant="subtitle2">{comment.author ? personName(comment.author, 'Unknown User') : 'System'}</Typography>
                              <Typography variant="caption" color="text.secondary">{formatDetectionDateTime(comment.createdAt)}</Typography>
                            </Box>
                          }
                          secondaryTypographyProps={{ component: 'div' }}
                          secondary={comment.isHtml ? (
                            <Box sx={{ mt: 1, '& p': { m: 0 } }} dangerouslySetInnerHTML={{ __html: comment.content }} />
                          ) : (
                            <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>{comment.content}</Typography>
                          )}
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>No comments yet</Typography>
                )}

                {canUpdate && (
                  <Box>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      placeholder="Add a comment..."
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      variant="outlined"
                      size="small"
                      sx={{ mb: 1 }}
                      inputProps={{ maxLength: 1000 }}
                    />
                    <Box display="flex" justifyContent="flex-end">
                      <Button startIcon={<SendIcon />} onClick={handleAddComment} disabled={!newComment.trim() || addingComment} variant="contained" size="small">
                        {addingComment ? 'Adding...' : 'Add'}
                      </Button>
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>

            <AskAiPanel
              targetType="detection"
              targetId={detection._id}
              aiAvailable={aiStatus.available}
              aiError={aiStatus.error}
              aiCode={aiStatus.code}
              compact
            />
          </Grid>
        </Grid>
      )}

      {activeTab === 1 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Raw Detection Data</Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Original alert data received by the detection pipeline
            </Typography>
            <Paper sx={{ p: 2, backgroundColor: 'grey.50', maxHeight: '600px', overflow: 'auto', fontFamily: 'monospace' }}>
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {detection.rawData
                  ? (typeof detection.rawData === 'string' ? detection.rawData : JSON.stringify(detection.rawData, null, 2))
                  : 'No raw data available'}
              </pre>
            </Paper>
          </CardContent>
        </Card>
      )}

      {/* Suppress dialog */}
      <Dialog open={suppressDialogOpen} onClose={savingSuppression ? undefined : () => setSuppressDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Suppress detection</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Suppressed detections stay in the system but are flagged as noise. Optionally set an expiry.
          </DialogContentText>
          <TextField
            label="Reason"
            fullWidth
            multiline
            minRows={2}
            value={suppressForm.reason}
            onChange={(e) => setSuppressForm((f) => ({ ...f, reason: e.target.value }))}
            sx={{ mb: 2 }}
            inputProps={{ maxLength: 500 }}
          />
          <TextField
            label="Suppress until (optional)"
            type="datetime-local"
            fullWidth
            value={suppressForm.until}
            onChange={(e) => setSuppressForm((f) => ({ ...f, until: e.target.value }))}
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSuppressDialogOpen(false)} disabled={savingSuppression}>Cancel</Button>
          <Button variant="contained" onClick={handleSuppress} disabled={savingSuppression}>
            {savingSuppression ? 'Saving...' : 'Suppress'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete dialog */}
      <Dialog open={deleteDialogOpen} onClose={deleting ? undefined : () => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete detection?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This permanently deletes "{detection.title}" including its comments and timeline. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={deleting} startIcon={deleting ? <CircularProgress size={14} color="inherit" /> : <DeleteIcon />}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DetectionDetailPage;
